import { useEffect, useState } from 'react';
import { useAuth, useLocale } from '../../context';
import { api } from '../../api';
import { formatPhoneInput, normalizePhone, phoneDigits } from '../../utils/phone';
import { clearAffiliateClickId, getAffiliateClickId } from '../../utils/affiliateClick';
import AuthCheckbox from './AuthCheckbox';
import AuthSocialProviders from './AuthSocialProviders';
import { buildAuthValidationCopy, mapAuthApiError, validateRegistrationStep1, validateRegistrationStep2 } from './authValidation';
import LoginVerification from './LoginVerification';

function IconAt() {
  return (
    <svg className="auth-input-icon svg-icon svg-icon--at" width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        d="M10 4.5a5.251 5.251 0 0 0-5.25 5.25A5.251 5.251 0 0 0 10 15c.492 0 .875.383.875.875a.864.864 0 0 1-.875.875c-3.855 0-7-3.145-7-7s3.145-7 7-7 7 3.145 7 7v.875a2.626 2.626 0 0 1-2.625 2.625 2.575 2.575 0 0 1-1.996-.93c-.629.575-1.45.93-2.379.93a3.49 3.49 0 0 1-3.5-3.5 3.49 3.49 0 0 1 3.5-3.5c.766 0 1.477.246 2.05.656a.83.83 0 0 1 .575-.218c.492 0 .875.382.875.875v3.062c0 .492.383.875.875.875a.864.864 0 0 0 .875-.875V9.75A5.251 5.251 0 0 0 10 4.5Zm1.75 5.25C11.75 8.793 10.957 8 10 8s-1.75.793-1.75 1.75.793 1.75 1.75 1.75 1.75-.793 1.75-1.75Z"
      />
    </svg>
  );
}

function IconKey() {
  return (
    <svg className="auth-input-icon svg-icon svg-icon--key-skeleton" width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12.188 4.5A2.194 2.194 0 0 0 10 6.688c0 1.203.984 2.187 2.188 2.187a2.194 2.194 0 0 0 2.187-2.188A2.194 2.194 0 0 0 12.187 4.5Zm0 6.125a4.016 4.016 0 0 1-2.106-.602L7.73 12.375l1.149 1.121a.927.927 0 0 1 0 1.258.927.927 0 0 1-1.258 0L6.5 13.605l-.52.52 1.149 1.121a.927.927 0 0 1 0 1.258.927.927 0 0 1-1.258 0l-1.75-1.75a.927.927 0 0 1 0-1.258l4.73-4.703a3.945 3.945 0 0 1-.601-2.105c0-2.16 1.777-3.938 3.938-3.938a3.938 3.938 0 0 1 3.937 3.938 3.921 3.921 0 0 1-3.938 3.937Z"
      />
    </svg>
  );
}

function IconUser() {
  return (
    <svg className="auth-input-icon svg-icon svg-icon--user" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 11.625a3.761 3.761 0 0 1-3.75-3.75A3.761 3.761 0 0 1 12 4.125a3.761 3.761 0 0 1 3.75 3.75 3.761 3.761 0 0 1-3.75 3.75Zm-.938 1.75h1.876c3.062 0 5.562 2.5 5.562 5.563a.92.92 0 0 1-.938.937H6.438a.92.92 0 0 1-.937-.938c0-3.062 2.5-5.562 5.563-5.562Z"
      />
    </svg>
  );
}

function IconEye({ open }) {
  if (open) {
    return (
      <svg className="svg-icon svg-icon--eye-slash" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          fill="currentColor"
          d="m4.281 3.219 3.157 3.156C8.688 5.562 10.217 5 12 5c2.531 0 4.531 1.156 6.031 2.531 1.438 1.344 2.438 2.969 2.907 4.094.093.25.093.531 0 .781-.47 1.094-1.47 2.719-2.907 4.094a.844.844 0 0 0-.25.219l3 3c.282.312.282.781 0 1.062a.719.719 0 0 1-1.062 0l-16.5-16.5a.774.774 0 0 1 0-1.062.774.774 0 0 1 1.062 0Zm5.125 5.125h-.031l1.094 1.062c.219-.125.5-.219.75-.312 1.594-.406 3.25.531 3.687 2.125.219.812.063 1.625-.312 2.312l1.062 1.094A4.501 4.501 0 0 0 12 7.5c-.969 0-1.875.313-2.594.844Zm3.75 8 2.031 2A8.18 8.18 0 0 1 12 19c-2.531 0-4.531-1.156-6-2.531-1.469-1.344-2.469-2.969-2.906-4.094a.797.797 0 0 1 0-.75c.344-.906 1.062-2.125 2.062-3.281l2.5 2.5c-.093.375-.156.75-.156 1.156 0 2.5 2.031 4.5 4.5 4.5.406 0 .781-.063 1.156-.156Z"
        />
      </svg>
    );
  }
  return (
    <svg className="svg-icon svg-icon--eye" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 5c2.531 0 4.563 1.156 6.031 2.531 1.438 1.344 2.438 2.969 2.907 4.094.093.25.093.5 0 .75-.47 1.125-1.47 2.75-2.907 4.094C16.563 17.844 14.531 19 12 19c-2.531 0-4.531-1.156-6.031-2.531C4.53 15.125 3.53 13.5 3.062 12.375c-.093-.25-.093-.5 0-.75.47-1.125 1.47-2.75 2.907-4.094C7.469 6.156 9.469 5 12 5Zm-4.5 7c0 2.5 2 4.5 4.5 4.5s4.5-2 4.5-4.5-2-4.5-4.5-4.5-4.5 2-4.5 4.5Zm4.5-2c0-.375-.094-.688-.25-1 1.406-.125 2.781.781 3.156 2.219.406 1.594-.531 3.25-2.125 3.687-1.594.406-3.25-.531-3.687-2.125A2.894 2.894 0 0 1 9 11.75c.313.156.656.25 1 .25 1.094 0 2-.906 2-2Z"
      />
    </svg>
  );
}

function IconUserPlus() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm0 1.75c-2.9 0-5.25 1.84-5.25 4.125C4.25 17.556 4.694 18 5.375 18h8.25c.681 0 1.125-.444 1.125-1.125 0-2.285-2.35-4.125-5.25-4.125ZM17.25 8a.75.75 0 0 1 .75.75V10h1.25a.75.75 0 0 1 0 1.5H18v1.25a.75.75 0 0 1-1.5 0V11.5H15.25a.75.75 0 0 1 0-1.5H16.5V8.75a.75.75 0 0 1 .75-.75Z"
      />
    </svg>
  );
}

function IconCheckBadge() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm4.28 7.47a.75.75 0 0 0-1.06-1.06l-4.72 4.72-1.72-1.72a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.06 0l5.25-5.25Z"
      />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.75 3.5A.75.75 0 0 0 10 2.75H6.75A2.75 2.75 0 0 0 4 5.5v13a2.75 2.75 0 0 0 2.75 2.75H10a.75.75 0 0 0 0-1.5H6.75c-.69 0-1.25-.56-1.25-1.25v-13c0-.69.56-1.25 1.25-1.25H10a.75.75 0 0 0 .75-.75Zm5.47 4.22a.75.75 0 1 0-1.06 1.06L17.44 11.5H9.75a.75.75 0 0 0 0 1.5h7.69l-2.28 2.72a.75.75 0 1 0 1.06 1.06l3.5-3.5a.75.75 0 0 0 0-1.06l-3.5-3.5Z"
      />
    </svg>
  );
}

const EMPTY_ERRORS = {
  username: '',
  email: '',
  password: '',
  phone: '',
  firstName: '',
  lastName: '',
  gender: '',
  birthDay: '',
  birthMonth: '',
  birthYear: '',
  address: '',
  ageRules: '',
  form: '',
};

const EXPIRED_ERRORS = new Set([
  'Doğrulama oturumu süresi doldu',
  'Doğrulama süresi doldu, tekrar giriş yapın',
]);

const GENDERS = [
  { id: 'male', label: 'Erkek' },
  { id: 'female', label: 'Kadın' },
  { id: 'unspecified', label: 'Belirtmek istemiyorum' },
];

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const MONTH_OPTIONS = [
  { value: '01', label: 'Ocak' },
  { value: '02', label: 'Şubat' },
  { value: '03', label: 'Mart' },
  { value: '04', label: 'Nisan' },
  { value: '05', label: 'Mayıs' },
  { value: '06', label: 'Haziran' },
  { value: '07', label: 'Temmuz' },
  { value: '08', label: 'Ağustos' },
  { value: '09', label: 'Eylül' },
  { value: '10', label: 'Ekim' },
  { value: '11', label: 'Kasım' },
  { value: '12', label: 'Aralık' },
];
const YEARS = Array.from({ length: 100 }, (_, i) => String(new Date().getFullYear() - 18 - i));

export default function RegistrationForm({
  onSuccess,
  onLoginClick,
  onOpenLegal,
  hideLoginLink = false,
  completeProfile = false,
  onLogout,
}) {
  const { setSession, token } = useAuth();
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const validationCopy = buildAuthValidationCopy(tr);

  const [formStep, setFormStep] = useState(completeProfile ? 2 : 1);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [currency, setCurrency] = useState('TRY');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [address, setAddress] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [ageRules, setAgeRules] = useState(false);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [loading, setLoading] = useState(false);
  const [authSettings, setAuthSettings] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('form');
  const [verifyStep, setVerifyStep] = useState('prompt');
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.authSettings()
      .then((settings) => {
        if (!cancelled) setAuthSettings(settings);
      })
      .catch(() => {
        if (!cancelled) {
          setAuthSettings({
            registrationEnabled: true,
            registerOtpEnabled: false,
            loginOtpEnabled: true,
            socialProviders: {
              google: { enabled: false, url: '' },
              telegram: { enabled: false, url: '' },
              x: { enabled: false, url: '' },
            },
          });
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!completeProfile) return;
    setFormStep(2);
  }, [completeProfile]);

  useEffect(() => {
    if (step !== 'verify' || !challenge?.expiresAt) return undefined;
    const expiresAt = new Date(challenge.expiresAt).getTime();
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      setStep('expired');
      return undefined;
    }
    const timer = setTimeout(() => setStep('expired'), remaining);
    return () => clearTimeout(timer);
  }, [step, challenge?.expiresAt]);

  const clearFieldError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const birthDate = birthYear && birthMonth && birthDay
    ? `${birthYear}-${birthMonth}-${birthDay}`
    : '';

  const finishRegister = (token, user) => {
    setSession(token, user);
    onSuccess?.();
  };

  const resetVerification = () => {
    setStep('form');
    setVerifyStep('prompt');
    setChallenge(null);
    setErrors(EMPTY_ERRORS);
  };

  const isExpiredError = (message) => EXPIRED_ERRORS.has(message);

  const goStep2 = (e) => {
    e.preventDefault();
    const nextErrors = validateRegistrationStep1({
      username,
      email,
      password,
      ageRules,
    }, validationCopy);
    if (Object.keys(nextErrors).length) {
      setErrors({ ...EMPTY_ERRORS, ...nextErrors });
      return;
    }
    setErrors(EMPTY_ERRORS);
    setFormStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    const step2Errors = validateRegistrationStep2({
      firstName,
      lastName,
      gender,
      birthDay,
      birthMonth,
      birthYear,
      phone,
      email,
      address,
    }, validationCopy);
    if (Object.keys(step2Errors).length) {
      setErrors({ ...EMPTY_ERRORS, ...step2Errors });
      return;
    }

    setErrors(EMPTY_ERRORS);
    setLoading(true);
    try {
      if (completeProfile) {
        const res = await api.completeProfile(token, {
          phone: normalizePhone(phone),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          gender,
          birthDate,
          address: address.trim(),
        });
        clearAffiliateClickId();
        finishRegister(res.token, res.user);
        return;
      }
      const normalizedPhoneValue = normalizePhone(phone) || undefined;
      const affiliateClickId = getAffiliateClickId();
      const res = await api.register({
        username: username.trim().toLowerCase(),
        currency,
        email: email.trim().toLowerCase() || undefined,
        password,
        phone: normalizedPhoneValue,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        gender,
        birthDate,
        address: address.trim(),
        promoCode: promoCode.trim() || undefined,
        click_id: affiliateClickId || undefined,
        btag: affiliateClickId || undefined,
      });
      if (res.verificationRequired) {
        setChallenge(res.challenge);
        setStep('verify');
        setVerifyStep('prompt');
        return;
      }
      clearAffiliateClickId();
        finishRegister(res.token, res.user);
    } catch (err) {
      const mapped = mapAuthApiError(err.message, validationCopy, err.message);
      setErrors({ ...EMPTY_ERRORS, ...mapped });
    } finally {
      setLoading(false);
    }
  };

  const handleGetCode = async () => {
    setLoading(true);
    setErrors(EMPTY_ERRORS);
    try {
      const sent = await api.sendRegisterCode({ challengeId: challenge.id });
      setVerifyStep('code');
      return sent;
    } catch (err) {
      if (isExpiredError(err.message)) {
        setStep('expired');
        throw err;
      }
      setErrors({ ...EMPTY_ERRORS, form: err.message });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code) => {
    setLoading(true);
    setErrors(EMPTY_ERRORS);
    try {
      const res = await api.verifyRegister({ challengeId: challenge.id, code });
      clearAffiliateClickId();
        finishRegister(res.token, res.user);
    } catch (err) {
      if (isExpiredError(err.message)) {
        setStep('expired');
        return;
      }
      setErrors({ ...EMPTY_ERRORS, form: err.message });
    } finally {
      setLoading(false);
    }
  };

  const openLegal = (pageId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenLegal?.(pageId);
  };

  if (authSettings && !authSettings.registrationEnabled) {
    return (
      <div className="registration-form__error" style={{ padding: '1rem 0' }}>
        Yeni kayıtlar şu an kapalı.
        {onLoginClick && (
          <div style={{ marginTop: '1rem' }}>
            <span
              className="button _ghostColor _sizeM _hasText _interactive login-link-button"
              role="button"
              tabIndex={0}
              onClick={onLoginClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onLoginClick();
              }}
            >
              {tr('auth.haveAccount')}
              {'\u00a0'}
              <span className="login-link-button__login-link">{tr('auth.login.link')}</span>
            </span>
          </div>
        )}
      </div>
    );
  }

  if (step === 'expired') {
    return (
      <div className="registration-form__error" style={{ padding: '1rem 0' }}>
        Doğrulama süresi doldu.
        <button type="button" className="button _accent _sizeL _hasText _interactive" style={{ marginTop: '1rem' }} onClick={resetVerification}>
          Tekrar dene
        </button>
      </div>
    );
  }

  if (step === 'verify' && challenge) {
    return (
      <LoginVerification
        challenge={challenge}
        step={verifyStep}
        onGetCode={handleGetCode}
        onCancel={resetVerification}
        onVerify={handleVerify}
        onResendCode={handleGetCode}
        loading={loading}
        error={errors.form}
      />
    );
  }

  return (
    <>
      <form className="form form--GGTvY" onSubmit={formStep === 1 ? goStep2 : submit} noValidate>
        <div className="registration-tabs" role="tablist" style={{ marginBottom: '12px' }}>
          {!completeProfile ? (
            <span className={`registration-tabs__item${formStep === 1 ? ' is-active' : ''}`}>1. Hesap</span>
          ) : null}
          <span className={`registration-tabs__item${formStep === 2 || completeProfile ? ' is-active' : ''}`}>
            {completeProfile ? 'Profil bilgileri' : '2. Profil'}
          </span>
        </div>

        {formStep === 1 && !completeProfile ? (
          <>
            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">Kullanıcı adı</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset inputLine_hasIcon${username ? ' inputLine_filled' : ''}${errors.username ? ' inputLine_error' : ''}`}>
                    <span className="inputLine__left"><IconUser /></span>
                    <div className="inputLine__content">
                      <input
                        type="text"
                        className="input"
                        name="username"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value.replace(/\s/g, ''));
                          clearFieldError('username');
                        }}
                        autoComplete="username"
                        maxLength={32}
                      />
                    </div>
                  </div>
                  {errors.username && <div className="bottomText bottomText_error">{errors.username}</div>}
                </div>
              </div>
            </div>

            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">{tr('auth.label.email')}</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset inputLine_hasIcon${email ? ' inputLine_filled' : ''}${errors.email ? ' inputLine_error' : ''}`}>
                    <span className="inputLine__left"><IconAt /></span>
                    <div className="inputLine__content">
                      <input
                        type="email"
                        className="input"
                        name="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          clearFieldError('email');
                        }}
                        placeholder=""
                        autoComplete="email"
                      />
                    </div>
                  </div>
                  {errors.email && <div className="bottomText bottomText_error">{errors.email}</div>}
                </div>
              </div>
            </div>

            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">{tr('auth.label.password')}</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset inputLine_hasIcon inputLine_hasRight${password ? ' inputLine_filled' : ''}${errors.password ? ' inputLine_error' : ''}`}>
                    <span className="inputLine__left"><IconKey /></span>
                    <div className="inputLine__content">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        className="input"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          clearFieldError('password');
                        }}
                        autoComplete="new-password"
                        maxLength={32}
                      />
                    </div>
                    <button
                      type="button"
                      className="inputLine__rightBtn"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <IconEye open={showPassword} />
                    </button>
                  </div>
                  {errors.password && <div className="bottomText bottomText_error">{errors.password}</div>}
                </div>
              </div>
            </div>

            <div className="form-row--xoHo9 form-row-promocode">
              {!showPromo ? (
                <div className="promocode-link-container">
                  <span
                    className="promocode-link"
                    onClick={() => setShowPromo(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setShowPromo(true);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {tr('auth.register.promoToggle')}
                  </span>
                </div>
              ) : (
                <div className="container--CCNx7 container_medium">
                  <div className="container__label">{tr('auth.label.promo')}</div>
                  <div className="inputContainer inputContainer_large">
                    <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${promoCode ? ' inputLine_filled' : ''}`}>
                      <div className="inputLine__content">
                        <input
                          type="text"
                          className="input"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-bottom--DWVny">
              <div className="form-row--xoHo9">
                <AuthCheckbox
                  checked={ageRules}
                  onChange={(v) => {
                    setAgeRules(v);
                    clearFieldError('ageRules');
                  }}
                  error={Boolean(errors.ageRules)}
                >
                  <span>{tr('auth.register.rulesBefore')}</span>
                  <a href="/pages/rules" className="description__link" onClick={openLegal('rules')}>{tr('auth.register.termsLink')}</a>
                  <span>{tr('auth.register.rulesAnd')}</span>
                  <a href="/pages/privacy-policy" className="description__link" onClick={openLegal('persdata')}>{tr('auth.register.privacyLink')}</a>
                  <span>{tr('auth.register.rulesAfter')}</span>
                </AuthCheckbox>
                {errors.ageRules && <div className="bottomText bottomText_error bottomText_checkbox">{errors.ageRules}</div>}
              </div>
              {errors.form && <div className="registration-form__error">{errors.form}</div>}
            </div>

            <div className="form-bottom-buttons--e4qpk">
              <div className="form-row--xoHo9">
                <button type="submit" className="button _accent _sizeL _hasText _interactive" disabled={!authSettings}>
                  Devam
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="form-row--xoHo9" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="container--CCNx7 container_medium">
                <div className="container__label">Ad</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${firstName ? ' inputLine_filled' : ''}${errors.firstName ? ' inputLine_error' : ''}`}>
                    <div className="inputLine__content">
                      <input
                        type="text"
                        className="input"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          clearFieldError('firstName');
                        }}
                        autoComplete="given-name"
                      />
                    </div>
                  </div>
                  {errors.firstName && <div className="bottomText bottomText_error">{errors.firstName}</div>}
                </div>
              </div>
              <div className="container--CCNx7 container_medium">
                <div className="container__label">Soyad</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${lastName ? ' inputLine_filled' : ''}${errors.lastName ? ' inputLine_error' : ''}`}>
                    <div className="inputLine__content">
                      <input
                        type="text"
                        className="input"
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          clearFieldError('lastName');
                        }}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                  {errors.lastName && <div className="bottomText bottomText_error">{errors.lastName}</div>}
                </div>
              </div>
            </div>

            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">Cinsiyet</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${gender ? ' inputLine_filled' : ''}${errors.gender ? ' inputLine_error' : ''}`}>
                    <div className="inputLine__content">
                      <select
                        className="input"
                        value={gender}
                        onChange={(e) => {
                          setGender(e.target.value);
                          clearFieldError('gender');
                        }}
                      >
                        <option value="">Seçin</option>
                        {GENDERS.map((g) => (
                          <option key={g.id} value={g.id}>{g.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {errors.gender && <div className="bottomText bottomText_error">{errors.gender}</div>}
                </div>
              </div>
            </div>

            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">Para birimi</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${currency ? ' inputLine_filled' : ''}`}>
                    <div className="inputLine__content">
                      <select
                        className="input"
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                      >
                        <option value="TRY">TRY (TL)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="USD">USD ($)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">Doğum tarihi</div>
                <div className="registration-birth-row">
                  <div className="inputContainer inputContainer_large">
                    <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${birthDay ? ' inputLine_filled' : ''}${errors.birthDay ? ' inputLine_error' : ''}`}>
                      <div className="inputLine__content">
                        <select
                          className="input"
                          value={birthDay}
                          onChange={(e) => {
                            setBirthDay(e.target.value);
                            clearFieldError('birthDay');
                          }}
                          aria-label="Gün"
                        >
                          <option value="">Gün</option>
                          {DAYS.map((d) => <option key={d} value={d}>{Number(d)}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="inputContainer inputContainer_large">
                    <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${birthMonth ? ' inputLine_filled' : ''}${errors.birthMonth || errors.birthDay ? ' inputLine_error' : ''}`}>
                      <div className="inputLine__content">
                        <select
                          className="input"
                          value={birthMonth}
                          onChange={(e) => {
                            setBirthMonth(e.target.value);
                            clearFieldError('birthMonth');
                            clearFieldError('birthDay');
                          }}
                          aria-label="Ay"
                        >
                          <option value="">Ay</option>
                          {MONTH_OPTIONS.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="inputContainer inputContainer_large">
                    <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${birthYear ? ' inputLine_filled' : ''}${errors.birthYear || errors.birthDay ? ' inputLine_error' : ''}`}>
                      <div className="inputLine__content">
                        <select
                          className="input"
                          value={birthYear}
                          onChange={(e) => {
                            setBirthYear(e.target.value);
                            clearFieldError('birthYear');
                            clearFieldError('birthDay');
                          }}
                          aria-label="Yıl"
                        >
                          <option value="">Yıl</option>
                          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {(errors.birthDay || errors.birthMonth || errors.birthYear) && (
                  <div className="bottomText bottomText_error">
                    {errors.birthDay || errors.birthMonth || errors.birthYear}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">{tr('auth.label.phoneOptional')}</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_rightOffset${phone ? ' inputLine_filled' : ''}${errors.phone ? ' inputLine_error' : ''}`}>
                    <div className="inputLine__content">
                      <input
                        type="tel"
                        className="input input-with-offset"
                        name="phone"
                        value={phone}
                        onChange={(e) => {
                          setPhone(formatPhoneInput(e.target.value));
                          clearFieldError('phone');
                        }}
                        autoComplete="tel"
                      />
                    </div>
                  </div>
                  {phoneDigits(phone).length > 0 ? (
                    <div className="bottomText">{tr('auth.register.smsHint')}</div>
                  ) : null}
                  {errors.phone && <div className="bottomText bottomText_error">{errors.phone}</div>}
                </div>
              </div>
            </div>

            <div className="form-row--xoHo9">
              <div className="container--CCNx7 container_medium">
                <div className="container__label">Adres</div>
                <div className="inputContainer inputContainer_large">
                  <div className={`inputLine inputLine_leftOffset inputLine_withoutBottomOffset${address ? ' inputLine_filled' : ''}${errors.address ? ' inputLine_error' : ''}`}>
                    <div className="inputLine__content">
                      <input
                        type="text"
                        className="input"
                        value={address}
                        onChange={(e) => {
                          setAddress(e.target.value);
                          clearFieldError('address');
                        }}
                        autoComplete="street-address"
                      />
                    </div>
                  </div>
                  {errors.address && <div className="bottomText bottomText_error">{errors.address}</div>}
                </div>
              </div>
            </div>

            {errors.form && <div className="registration-form__error">{errors.form}</div>}

            <div className="form-bottom-buttons--e4qpk">
              {completeProfile ? (
                <div className="form-row--xoHo9 registration-complete-actions">
                  <button
                    type="submit"
                    className="auth__submit-button btn btn--md btn--weighted registration-icon-btn registration-icon-btn--primary"
                    disabled={loading}
                  >
                    <span className="registration-icon-btn__text">
                      {loading ? '...' : (locale === 'en' ? 'Complete Registration' : 'Kaydı Tamamla')}
                    </span>
                    <span className="registration-icon-btn__icon" aria-hidden="true">
                      <IconCheckBadge />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="auth__submit-button btn btn--md btn--weighted registration-icon-btn registration-icon-btn--ghost"
                    disabled={loading}
                    onClick={() => onLogout?.()}
                  >
                    <span className="registration-icon-btn__text">
                      {locale === 'en' ? 'Sign Out' : 'Çıkış Yap'}
                    </span>
                    <span className="registration-icon-btn__icon" aria-hidden="true">
                      <IconLogout />
                    </span>
                  </button>
                </div>
              ) : (
                <div className="form-row--xoHo9 registration-step-actions">
                  <button
                    type="button"
                    className="button _secondary _sizeL _hasText _interactive"
                    disabled={loading}
                    onClick={() => {
                      setFormStep(1);
                      setErrors((prev) => ({ ...prev, form: '' }));
                    }}
                  >
                    1. adıma dön
                  </button>
                  <button
                    type="submit"
                    className="auth__submit-button btn btn--md btn--weighted registration-icon-btn registration-icon-btn--primary"
                    disabled={loading || !authSettings}
                  >
                    <span className="registration-icon-btn__text">
                      {loading ? '...' : 'Üye ol'}
                    </span>
                    <span className="registration-icon-btn__icon" aria-hidden="true">
                      <IconUserPlus />
                    </span>
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </form>

      {formStep === 1 && step === 'form' && !completeProfile ? (
        <div className="auth__social">
          <AuthSocialProviders providers={authSettings?.socialProviders} />
        </div>
      ) : null}

      {onLoginClick && !hideLoginLink && !completeProfile && (
        <span
          className="button _ghostColor _sizeM _hasText _interactive login-link-button"
          role="button"
          tabIndex={0}
          onClick={onLoginClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onLoginClick();
          }}
        >
          {tr('auth.haveAccount')}
          {'\u00a0'}
          <span className="login-link-button__login-link">{tr('auth.login.link')}</span>
        </span>
      )}
    </>
  );
}
